import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import EmptyState from "../components/EmptyState";

const fmt = (n) =>
  `$${parseFloat(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = String(d).split("-");
  return new Date(Number(y), Number(m) - 1, Number(day)).toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const STATUS_STYLES = {
  pending: { bg: "#faeeda", color: "#633806", label: "Pending" },
  in_transit: { bg: "#e6f1fb", color: "#0c447c", label: "In transit" },
  delivered: { bg: "#eaf3de", color: "#3b6d11", label: "Delivered" },
};

const inputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
  background: "#fff",
};

const EMPTY_FORM = {
  trip_id: "",
  driver_id: "",
  driver_name: "",
  pickup: "",
  pickup_date: "",
  dropoff: "",
  dropoff_date: "",
  miles: "",
  gross: "",
  status: "pending",
  notes: "",
};

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: "10px",
      fontWeight: "500",
      color: "#9ca3af",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      marginBottom: "10px",
    }}
  >
    {children}
  </div>
);

// ── Driver lookup ──
function DriverLookup({ value, onSelect, drivers }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    setQuery(value || "");
  }, [value]);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = drivers
    .filter((d) =>
      `${d.first_name} ${d.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase())
    )
    .slice(0, 6);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        placeholder="Search driver name..."
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value) onSelect(null);
        }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {open && query.length > 0 && filtered.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "0.5px solid #e0e0e0",
            borderRadius: "8px",
            zIndex: 200,
            marginTop: "4px",
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          {filtered.map((d) => (
            <div
              key={d.id}
              onMouseDown={() => {
                onSelect(d);
                setQuery(`${d.first_name} ${d.last_name}`);
                setOpen(false);
              }}
              style={{
                padding: "9px 14px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#111",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              <div
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  background: "#eaf3de",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "#3a9e5f",
                  flexShrink: 0,
                }}
              >
                {d.first_name?.[0]}
                {d.last_name?.[0]}
              </div>
              <div>
                <div style={{ fontWeight: "500" }}>
                  {d.first_name} {d.last_name}
                </div>
                {d.corp_name && (
                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {d.corp_name}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Driver filter lookup ──
function DriverFilterLookup({ drivers, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = drivers.find((d) => d.id === value);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = drivers
    .filter((d) =>
      `${d.first_name} ${d.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase())
    )
    .slice(0, 8);
  const displayValue = open
    ? query
    : selected
    ? `${selected.first_name} ${selected.last_name}`
    : query;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={displayValue}
        placeholder="Filter by driver..."
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value) {
            onChange("");
            setQuery("");
          }
        }}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        style={{
          ...inputStyle,
          width: "180px",
          padding: "6px 10px",
          fontSize: "12px",
        }}
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            background: "#fff",
            border: "0.5px solid #e0e0e0",
            borderRadius: "8px",
            zIndex: 200,
            marginTop: "4px",
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            minWidth: "200px",
          }}
        >
          <div
            onMouseDown={() => {
              onChange("");
              setQuery("");
              setOpen(false);
            }}
            style={{
              padding: "9px 14px",
              cursor: "pointer",
              fontSize: "12px",
              color: "#9ca3af",
              borderBottom: "0.5px solid #f0f0f0",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            All drivers
          </div>
          {filtered.map((d) => (
            <div
              key={d.id}
              onMouseDown={() => {
                onChange(d.id);
                setQuery("");
                setOpen(false);
              }}
              style={{
                padding: "9px 14px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#111",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: value === d.id ? "#f0faf4" : "#fff",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  value === d.id ? "#f0faf4" : "#fff")
              }
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: "#eaf3de",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "9px",
                  fontWeight: "500",
                  color: "#3a9e5f",
                  flexShrink: 0,
                }}
              >
                {d.first_name?.[0]}
                {d.last_name?.[0]}
              </div>
              <div style={{ fontWeight: value === d.id ? "500" : "400" }}>
                {d.first_name} {d.last_name}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div
              style={{
                padding: "9px 14px",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              No drivers found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── City lookup ──
function CityLookup({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  useEffect(() => {
    setQuery(value || "");
  }, [value]);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            q
          )}&countrycodes=us&limit=6&format=json&addressdetails=1`
        );
        const data = await res.json();
        const cities = data
          .filter(
            (r) =>
              r.address?.city ||
              r.address?.town ||
              r.address?.village ||
              r.address?.county
          )
          .map((r) => {
            const city =
              r.address?.city ||
              r.address?.town ||
              r.address?.village ||
              r.address?.county ||
              "";
            return `${city}, ${r.address?.state || ""}`;
          })
          .filter((v, i, arr) => arr.indexOf(v) === i);
        setSuggestions(cities);
        setOpen(true);
      } catch (e) {
        setSuggestions([]);
      }
      setLoading(false);
    }, 350);
  };
  const clear = (e) => {
    e.stopPropagation();
    setQuery("");
    onChange("");
    setSuggestions([]);
  };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{ position: "relative", display: "flex", alignItems: "center" }}
      >
        <span
          style={{
            position: "absolute",
            left: "10px",
            color: "#9ca3af",
            display: "flex",
            pointerEvents: "none",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          placeholder={placeholder || "Search city, state..."}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            fetchSuggestions(e.target.value);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          style={{
            ...inputStyle,
            paddingLeft: "32px",
            paddingRight: query ? "30px" : "12px",
          }}
        />
        {query && (
          <button
            onClick={clear}
            style={{
              position: "absolute",
              right: "8px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              padding: "2px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#555")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
      {open && (suggestions.length > 0 || loading) && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "0.5px solid #e0e0e0",
            borderRadius: "8px",
            zIndex: 200,
            marginTop: "4px",
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          {loading && (
            <div
              style={{
                padding: "10px 14px",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              Searching...
            </div>
          )}
          {!loading &&
            suggestions.map((city, i) => (
              <div
                key={i}
                onMouseDown={() => {
                  setQuery(city);
                  onChange(city);
                  setSuggestions([]);
                  setOpen(false);
                }}
                style={{
                  padding: "9px 14px",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#111",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom:
                    i < suggestions.length - 1 ? "0.5px solid #f5f5f5" : "none",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {city}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ── Detail panel ──
function DetailPanel({ load, onClose, onEdit, onStatusChange }) {
  const st = STATUS_STYLES[load.status] || STATUS_STYLES.pending;
  const dName = load.drivers
    ? `${load.drivers.first_name} ${load.drivers.last_name}`
    : "—";
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const changeStatus = async (newStatus) => {
    setUpdatingStatus(true);
    await supabase
      .from("loads_manual")
      .update({ status: newStatus })
      .eq("id", load.id);
    onStatusChange(load.id, newStatus);
    setUpdatingStatus(false);
  };

  // Timeline items
  const timeline = [
    {
      done: true,
      color: "#eaf3de",
      iconColor: "#3b6d11",
      icon: (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      title: "Load created",
      sub: fmtDateTime(load.created_at),
    },
    {
      done: !!load.pickup_date,
      color: load.pickup_date ? "#e6f1fb" : "#f0f0f0",
      iconColor: load.pickup_date ? "#185fa5" : "#9ca3af",
      icon: (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="1" y="3" width="15" height="13" rx="1" />
          <path d="M16 8h4l3 5v4h-7V8z" />
          <circle cx="5.5" cy="18.5" r="2.5" />
          <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
      ),
      title: "Picked up",
      sub: load.pickup_date
        ? `${load.pickup || ""} · ${fmtDate(load.pickup_date)}`
        : "Not yet picked up",
    },
    {
      done: load.status === "delivered",
      color: load.status === "delivered" ? "#eaf3de" : "#f0f0f0",
      iconColor: load.status === "delivered" ? "#3b6d11" : "#9ca3af",
      icon: (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
      title: "Delivered",
      sub: load.dropoff_date
        ? `${load.dropoff || ""} · ${fmtDate(load.dropoff_date)}`
        : load.status === "delivered"
        ? load.dropoff || "—"
        : "Pending delivery",
    },
  ];

  return (
    <div
      style={{
        width: "320px",
        flexShrink: 0,
        background: "#fff",
        borderLeft: "0.5px solid #e8e8e8",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "sticky",
        top: 0,
        overflowY: "auto",
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: "14px 16px",
          borderBottom: "0.5px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div>
          <div style={{ fontSize: "14px", fontWeight: "500", color: "#111" }}>
            {load.trip_id || "Load detail"}
          </div>
          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "1px" }}>
            {dName}
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button
            onClick={() => onEdit(load)}
            style={{
              background: "#f0f0f0",
              border: "none",
              cursor: "pointer",
              fontSize: "11px",
              color: "#555",
              fontWeight: "500",
              padding: "4px 10px",
              borderRadius: "6px",
              fontFamily: "inherit",
            }}
          >
            ✏️ Edit
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              color: "#9ca3af",
              lineHeight: 1,
              padding: "2px 4px",
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "0.5px solid #e8e8e8",
          background: "#fafafa",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Status
          </span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "500",
              padding: "2px 8px",
              borderRadius: "20px",
              background: st.bg,
              color: st.color,
            }}
          >
            {st.label}
          </span>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          {Object.entries(STATUS_STYLES).map(([key, s]) => (
            <button
              key={key}
              disabled={updatingStatus || load.status === key}
              onClick={() => changeStatus(key)}
              style={{
                fontSize: "11px",
                fontWeight: "500",
                padding: "4px 10px",
                borderRadius: "6px",
                border: "none",
                cursor: load.status === key ? "default" : "pointer",
                fontFamily: "inherit",
                background: load.status === key ? s.bg : "#f0f0f0",
                color: load.status === key ? s.color : "#6b7280",
                opacity: updatingStatus ? 0.5 : 1,
                transition: "all 0.15s",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Route */}
        <SectionLabel>Route</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: "6px",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              background: "#f8f8f8",
              borderRadius: "8px",
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                color: "#9ca3af",
                marginBottom: "3px",
              }}
            >
              Pickup
            </div>
            <div style={{ fontSize: "12px", fontWeight: "500", color: "#111" }}>
              {load.pickup || "—"}
            </div>
            {load.pickup_date && (
              <div
                style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}
              >
                {fmtDate(load.pickup_date)}
              </div>
            )}
          </div>
          <div
            style={{ color: "#d1d5db", fontSize: "16px", textAlign: "center" }}
          >
            →
          </div>
          <div
            style={{
              background: "#f8f8f8",
              borderRadius: "8px",
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                color: "#9ca3af",
                marginBottom: "3px",
              }}
            >
              Dropoff
            </div>
            <div style={{ fontSize: "12px", fontWeight: "500", color: "#111" }}>
              {load.dropoff || "—"}
            </div>
            {load.dropoff_date && (
              <div
                style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}
              >
                {fmtDate(load.dropoff_date)}
              </div>
            )}
          </div>
        </div>

        {/* Details grid */}
        <SectionLabel>Details</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {[
            {
              label: "Miles",
              value: load.miles != null ? String(load.miles) : "—",
            },
            {
              label: "Gross",
              value: load.gross != null ? fmt(load.gross) : "—",
              green: true,
            },
            { label: "Driver", value: dName },
            { label: "Added", value: fmtDate(load.created_at?.split("T")[0]) },
          ].map(({ label, value, green }) => (
            <div key={label}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: "3px",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: green ? "500" : "400",
                  color: green ? "#3b6d11" : "#111",
                  fontFamily:
                    label === "Miles" || label === "Gross"
                      ? "var(--font-mono, monospace)"
                      : "inherit",
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <SectionLabel>Timeline</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {timeline.map((item, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "28px",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    background: item.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: item.iconColor,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                {i < timeline.length - 1 && (
                  <div
                    style={{
                      width: "1px",
                      background: "#e8e8e8",
                      flex: 1,
                      minHeight: "20px",
                      margin: "3px 0",
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  paddingBottom: i < timeline.length - 1 ? "16px" : "0",
                  paddingTop: "4px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: item.done ? "#111" : "#9ca3af",
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    marginTop: "1px",
                  }}
                >
                  {item.sub}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        {load.notes && (
          <div
            style={{
              marginTop: "20px",
              paddingTop: "16px",
              borderTop: "0.5px solid #f0f0f0",
            }}
          >
            <SectionLabel>Notes</SectionLabel>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                background: "#f8f8f8",
                borderRadius: "8px",
                padding: "10px 12px",
                lineHeight: "1.6",
              }}
            >
              {load.notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Loads() {
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [search, setSearch] = useState("");
  const [filterDriver, setFilterDriver] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editLoad, setEditLoad] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);

  useEffect(() => {
    fetchLoads();
    fetchDrivers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLoads = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("loads_manual")
      .select("*, drivers(first_name, last_name, corp_name)")
      .order("created_at", { ascending: false });
    setLoads(data || []);
    setLoading(false);
  };

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("id, first_name, last_name, corp_name")
      .order("first_name");
    setDrivers(data || []);
  };

  const openAdd = () => {
    setEditLoad(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (load) => {
    setEditLoad(load);
    setForm({
      trip_id: load.trip_id || "",
      driver_id: load.driver_id || "",
      driver_name: load.drivers
        ? `${load.drivers.first_name} ${load.drivers.last_name}`
        : "",
      pickup: load.pickup || "",
      pickup_date: load.pickup_date || "",
      dropoff: load.dropoff || "",
      dropoff_date: load.dropoff_date || "",
      miles: load.miles != null ? String(load.miles) : "",
      gross: load.gross != null ? String(load.gross) : "",
      status: load.status || "pending",
      notes: load.notes || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.driver_id) {
      alert("Please select a driver");
      return;
    }
    setSaving(true);
    const payload = {
      trip_id: form.trip_id.trim() || null,
      driver_id: form.driver_id,
      pickup: form.pickup.trim() || null,
      pickup_date: form.pickup_date || null,
      dropoff: form.dropoff.trim() || null,
      dropoff_date: form.dropoff_date || null,
      miles: form.miles ? parseFloat(form.miles) : null,
      gross: form.gross ? parseFloat(form.gross) : null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    if (editLoad) {
      await supabase.from("loads_manual").update(payload).eq("id", editLoad.id);
    } else {
      await supabase.from("loads_manual").insert(payload);
    }
    setShowModal(false);
    setSelectedLoad(null);
    fetchLoads();
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await supabase.from("loads_manual").delete().eq("id", id);
    setDeleteConfirm(null);
    if (selectedLoad?.id === id) setSelectedLoad(null);
    fetchLoads();
  };

  const handleStatusChange = (id, newStatus) => {
    setLoads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, status: newStatus } : l))
    );
    if (selectedLoad?.id === id)
      setSelectedLoad((prev) => ({ ...prev, status: newStatus }));
  };

  const handleRowClick = (load) => {
    setSelectedLoad((prev) => (prev?.id === load.id ? null : load));
  };

  const filtered = loads.filter((l) => {
    const dName = l.drivers
      ? `${l.drivers.first_name} ${l.drivers.last_name}`
      : "";
    const matchSearch =
      !search ||
      (l.trip_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.pickup || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.dropoff || "").toLowerCase().includes(search.toLowerCase()) ||
      dName.toLowerCase().includes(search.toLowerCase());
    const matchDriver = !filterDriver || l.driver_id === filterDriver;
    const matchStatus = !filterStatus || l.status === filterStatus;
    return matchSearch && matchDriver && matchStatus;
  });

  const totalGross = loads.reduce((s, l) => s + parseFloat(l.gross || 0), 0);
  const inTransit = loads.filter((l) => l.status === "in_transit").length;
  const pending = loads.filter((l) => l.status === "pending").length;

  const btnStyle = (bg, color, disabled = false) => ({
    background: bg,
    color,
    fontSize: "13px",
    fontWeight: "500",
    padding: "7px 16px",
    borderRadius: "8px",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    fontFamily: "inherit",
  });

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <div
        style={{
          fontSize: "11px",
          fontWeight: "500",
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: "4px",
        }}
      >
        {label}
      </div>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        style={inputStyle}
      />
    </div>
  );

  const COLS = "100px 130px 160px 160px 55px 80px 90px auto";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMobile && <MobileHeader title="Loads & Trips" />}

        {/* Top bar */}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="t-page-title">Loads & Trips</div>
          <button onClick={openAdd} style={btnStyle("#1a1e1c", "#fff")}>
            + Add load
          </button>
        </div>

        {/* Main area — table + detail panel side by side */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "12px" : "20px 24px",
            }}
          >
            {/* Stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              {[
                {
                  label: "Total loads",
                  value: String(loads.length),
                  color: "#111",
                },
                {
                  label: "Total gross",
                  value: fmt(totalGross),
                  color: "#3b6d11",
                },
                {
                  label: "In transit",
                  value: String(inTransit),
                  color: "#185fa5",
                },
                { label: "Pending", value: String(pending), color: "#854f0b" },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  style={{
                    background: "#fff",
                    border: "0.5px solid #e8e8e8",
                    borderRadius: "10px",
                    padding: "12px 16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "4px",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Table card */}
            <div
              style={{
                background: "#fff",
                border: "0.5px solid #e8e8e8",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              {/* Filters */}
              <div
                style={{
                  padding: "10px 16px",
                  borderBottom: "0.5px solid #e8e8e8",
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <input
                  placeholder="Search trip ID, city, driver..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: isMobile ? "100%" : "220px",
                    padding: "6px 10px",
                    fontSize: "12px",
                  }}
                />
                <DriverFilterLookup
                  drivers={drivers}
                  value={filterDriver}
                  onChange={setFilterDriver}
                />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "auto",
                    padding: "6px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_transit">In transit</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>

              {/* Table header */}
              {!isMobile && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: COLS,
                    padding: "8px 16px",
                    background: "#fafafa",
                    borderBottom: "0.5px solid #e8e8e8",
                  }}
                >
                  {[
                    "Trip ID",
                    "Driver",
                    "Pickup",
                    "Dropoff",
                    "Miles",
                    "Gross",
                    "Status",
                    "",
                  ].map((h) => (
                    <div key={h} className="t-table-header">
                      {h}
                    </div>
                  ))}
                </div>
              )}

              {/* Rows */}
              {loading ? (
                <div
                  style={{ padding: "40px", textAlign: "center" }}
                  className="t-body"
                >
                  Loading...
                </div>
              ) : filtered.length === 0 ? (
                loads.length === 0 ? (
                  <EmptyState
                    title="No Records Found"
                    subtitle="Add your first trip to start tracking loads."
                    ctaLabel="+ Add load"
                    onCta={openAdd}
                  />
                ) : (
                  <EmptyState
                    title="No Records Found"
                    subtitle="Try adjusting your search or filters."
                  />
                )
              ) : (
                filtered.map((load, i) => {
                  const st =
                    STATUS_STYLES[load.status] || STATUS_STYLES.pending;
                  const dName = load.drivers
                    ? `${load.drivers.first_name} ${load.drivers.last_name}`
                    : "—";
                  const isSelected = selectedLoad?.id === load.id;
                  return isMobile ? (
                    <div
                      key={load.id}
                      onClick={() => handleRowClick(load)}
                      style={{
                        padding: "12px 16px",
                        borderBottom: "0.5px solid #f0f0f0",
                        background: isSelected ? "#f0faf4" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "6px",
                        }}
                      >
                        <div>
                          <div className="t-name">{dName}</div>
                          <div className="t-data" style={{ color: "#9ca3af" }}>
                            {load.trip_id || "—"}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            background: st.bg,
                            color: st.color,
                          }}
                        >
                          {st.label}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div className="t-label" style={{ color: "#6b7280" }}>
                          {load.pickup || "—"} → {load.dropoff || "—"}
                        </div>
                        <div
                          className="t-data"
                          style={{ color: "#3b6d11", fontWeight: "500" }}
                        >
                          {load.gross != null ? fmt(load.gross) : "—"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={load.id}
                      onClick={() => handleRowClick(load)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: COLS,
                        padding: "11px 16px",
                        borderBottom: "0.5px solid #f0f0f0",
                        alignItems: "center",
                        background: isSelected
                          ? "#f0faf4"
                          : i % 2 === 1
                          ? "#fafafa"
                          : "#fff",
                        cursor: "pointer",
                        transition: "background 0.1s",
                        borderLeft: isSelected
                          ? "3px solid #3a9e5f"
                          : "3px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background =
                            i % 2 === 1 ? "#fafafa" : "#fff";
                      }}
                    >
                      <div className="t-data" style={{ color: "#111" }}>
                        {load.trip_id || "—"}
                      </div>
                      <div>
                        <div className="t-name" style={{ fontSize: "12px" }}>
                          {dName}
                        </div>
                        {load.drivers?.corp_name && (
                          <div className="t-label" style={{ color: "#9ca3af" }}>
                            {load.drivers.corp_name}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="t-body" style={{ fontSize: "12px" }}>
                          {load.pickup || "—"}
                        </div>
                        {load.pickup_date && (
                          <div className="t-label" style={{ color: "#9ca3af" }}>
                            {fmtDate(load.pickup_date)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="t-body" style={{ fontSize: "12px" }}>
                          {load.dropoff || "—"}
                        </div>
                        {load.dropoff_date && (
                          <div className="t-label" style={{ color: "#9ca3af" }}>
                            {fmtDate(load.dropoff_date)}
                          </div>
                        )}
                      </div>
                      <div className="t-data" style={{ color: "#374151" }}>
                        {load.miles != null ? load.miles : "—"}
                      </div>
                      <div
                        className="t-data"
                        style={{ color: "#3b6d11", fontWeight: "500" }}
                      >
                        {load.gross != null ? fmt(load.gross) : "—"}
                      </div>
                      <div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            background: st.bg,
                            color: st.color,
                          }}
                        >
                          {st.label}
                        </span>
                      </div>
                      <div
                        style={{ display: "flex", gap: "6px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => openEdit(load)}
                          style={{
                            background: "#f0f0f0",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "11px",
                            color: "#3a9e5f",
                            fontWeight: "500",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontFamily: "inherit",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(load)}
                          style={{
                            background: "#fcebeb",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "11px",
                            color: "#a32d2d",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontFamily: "inherit",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}

              <div
                className="t-label"
                style={{ padding: "10px 16px", color: "#9ca3af" }}
              >
                {filtered.length} load{filtered.length !== 1 ? "s" : ""}
                {filterDriver || filterStatus || search
                  ? " (filtered)"
                  : " total"}
              </div>
            </div>
          </div>

          {/* Detail panel */}
          {selectedLoad && !isMobile && (
            <DetailPanel
              load={selectedLoad}
              onClose={() => setSelectedLoad(null)}
              onEdit={(load) => {
                openEdit(load);
              }}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      {/* Mobile detail panel as modal */}
      {selectedLoad && isMobile && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 900,
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedLoad(null);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px 16px 0 0",
              width: "100%",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <DetailPanel
              load={selectedLoad}
              onClose={() => setSelectedLoad(null)}
              onEdit={(load) => {
                setSelectedLoad(null);
                openEdit(load);
              }}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "0.5px solid #e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div className="t-name" style={{ fontSize: "15px" }}>
                  {editLoad ? "Edit load" : "Add load"}
                </div>
                <div
                  className="t-label"
                  style={{ color: "#9ca3af", marginTop: "2px" }}
                >
                  Manually {editLoad ? "update" : "add"} a trip
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: "#f0f0f0",
                  border: "none",
                  borderRadius: "8px",
                  width: "30px",
                  height: "30px",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              {field("Trip ID", "trip_id", "text", "e.g. TRP-1042")}
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Driver *
                </div>
                <DriverLookup
                  value={form.driver_name}
                  drivers={drivers}
                  onSelect={(d) =>
                    setForm((p) => ({
                      ...p,
                      driver_id: d?.id || "",
                      driver_name: d ? `${d.first_name} ${d.last_name}` : "",
                    }))
                  }
                />
                {form.driver_id && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "11px",
                      color: "#3a9e5f",
                    }}
                  >
                    ✓ Driver selected
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "4px",
                    }}
                  >
                    Pickup city
                  </div>
                  <CityLookup
                    value={form.pickup}
                    onChange={(v) => setForm((p) => ({ ...p, pickup: v }))}
                    placeholder="e.g. Columbus, OH"
                  />
                </div>
                {field("Pickup date", "pickup_date", "date")}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "4px",
                    }}
                  >
                    Dropoff city
                  </div>
                  <CityLookup
                    value={form.dropoff}
                    onChange={(v) => setForm((p) => ({ ...p, dropoff: v }))}
                    placeholder="e.g. Chicago, IL"
                  />
                </div>
                {field("Dropoff date", "dropoff_date", "date")}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {field("Miles", "miles", "number", "e.g. 420")}
                {field("Gross ($)", "gross", "number", "e.g. 2800")}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Status
                </div>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: e.target.value }))
                  }
                  style={{ ...inputStyle, cursor: "pointer" }}
                >
                  <option value="pending">Pending</option>
                  <option value="in_transit">In transit</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Notes (optional)
                </div>
                <textarea
                  value={form.notes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  placeholder="Any additional notes..."
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: "70px",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                borderTop: "0.5px solid #e8e8e8",
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              {editLoad && (
                <button
                  onClick={() => setDeleteConfirm(editLoad)}
                  style={{
                    ...btnStyle("#fcebeb", "#a32d2d"),
                    marginRight: "auto",
                  }}
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => setShowModal(false)}
                style={btnStyle("#f0f0f0", "#555")}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={btnStyle(saving ? "#9ca3af" : "#3a9e5f", "#fff", saving)}
              >
                {saving ? "Saving..." : editLoad ? "Save changes" : "Add load"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "340px",
              width: "90%",
            }}
          >
            <div
              className="t-name"
              style={{ marginBottom: "8px", fontSize: "15px" }}
            >
              Delete load?
            </div>
            <div
              className="t-body"
              style={{ color: "#6b7280", marginBottom: "20px" }}
            >
              Trip {deleteConfirm.trip_id || "this load"} will be permanently
              deleted.
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                style={btnStyle("#a32d2d", "#fff")}
              >
                Yes, delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={btnStyle("#f0f0f0", "#555")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
